import { computed, effect, inject, Injectable, Signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

/**
 * Geraeteklasse — die EINE zentrale Wahrheit fuer responsive Entscheidungen.
 * Schwellen siehe {@link DESKTOP_MIN} / {@link MOBILE_MAX}.
 */
export enum DeviceClass {
	/** Reine Mobilansicht (< 600px) — Sidenav wird zum Burger. */
	Mobile = 'mobile',
	/** Tablet / schmaler Laptop (600–1279px) — araHk4 zeigt den Icon-Rail. */
	Tablet = 'tablet',
	/** Desktop (>= 1280px) — volle Sidenav. */
	Desktop = 'desktop',
}

/** Ab dieser Breite gilt "Desktop" (volle Sidenav). */
export const DESKTOP_MIN = 1280;
/** Bis zu dieser Breite gilt "reine Mobilansicht" (Burger). */
export const MOBILE_MAX = 599;

const DESKTOP_QUERY = `(min-width: ${DESKTOP_MIN}px)`;
const MOBILE_QUERY = `(max-width: ${MOBILE_MAX}px)`;
const TABLET_QUERY = `(min-width: ${MOBILE_MAX + 1}px) and (max-width: ${DESKTOP_MIN - 1}px)`;

/**
 * Zentrale Responsive-Erkennung (Single Source of Truth).
 *
 * Signal-first: {@link device} + die abgeleiteten `is*`-Signals sind die bevorzugte API
 * (in Templates als `x()` aufrufbar, reagieren auf Resize). Fuer die wenigen verbliebenen
 * RxJS-Consumer (layout.component, footer) gibt es schmale Observable-Bruecken.
 *
 * Frueher war die Erkennung im LayoutService verstreut und widerspruechlich
 * (`isMobile()` = ≤480 vs `isMobile$` = ≤599). LayoutService delegiert seine
 * Detection-Member jetzt hierher.
 */
@Injectable({ providedIn: 'root' })
export class ResponsiveService {
	private readonly breakpointObserver = inject(BreakpointObserver);
	private readonly document = inject(DOCUMENT);

	/** Roh-Matches der drei Media-Queries; mit synchronem Initialwert (kein `undefined`-Erst-Read). */
	private readonly state = toSignal(
		this.breakpointObserver.observe([DESKTOP_QUERY, MOBILE_QUERY, TABLET_QUERY]).pipe(
			map((result) => ({
				desktop: result.breakpoints[DESKTOP_QUERY],
				mobile: result.breakpoints[MOBILE_QUERY],
				tablet: result.breakpoints[TABLET_QUERY],
			}))
		),
		{
			initialValue: {
				desktop: this.breakpointObserver.isMatched(DESKTOP_QUERY),
				mobile: this.breakpointObserver.isMatched(MOBILE_QUERY),
				tablet: this.breakpointObserver.isMatched(TABLET_QUERY),
			},
		}
	);

	/** Aktuelle Geraeteklasse. */
	public readonly device: Signal<DeviceClass> = computed((): DeviceClass => {
		const s = this.state();
		if (s.desktop) return DeviceClass.Desktop;
		if (s.mobile) return DeviceClass.Mobile;
		return DeviceClass.Tablet;
	});

	/** Reine Mobilansicht (< 600px). */
	public readonly isMobile: Signal<boolean> = computed((): boolean => this.device() === DeviceClass.Mobile);
	/** Tablet (600–1279px). */
	public readonly isTablet: Signal<boolean> = computed((): boolean => this.device() === DeviceClass.Tablet);
	/** Desktop (>= 1280px). */
	public readonly isDesktop: Signal<boolean> = computed((): boolean => this.device() === DeviceClass.Desktop);
	/** Alles unterhalb Desktop (Mobile ODER Tablet) — entspricht der alten "< lg"-Kante (≤1279). */
	public readonly ltDesktop: Signal<boolean> = computed((): boolean => this.device() !== DeviceClass.Desktop);

	// Observable-Bruecken fuer die verbliebenen RxJS-Consumer.
	public readonly isDesktop$: Observable<boolean> = toObservable(this.isDesktop);
	public readonly ltLg$: Observable<boolean> = toObservable(this.ltDesktop);

	constructor() {
		// Spiegelt die Geraeteklasse als Body-Klasse → CSS reagiert ZENTRAL darauf
		// (body.ara-mobile / -tablet / -desktop), ohne eigenes hartcodiertes @media.
		// EINE Quelle (MOBILE_MAX/DESKTOP_MIN) steuert damit CSS UND TS (@if isMobile()).
		effect((): void => {
			const device = this.device();
			const classList = this.document.body.classList;
			classList.toggle('ara-mobile', device === DeviceClass.Mobile);
			classList.toggle('ara-tablet', device === DeviceClass.Tablet);
			classList.toggle('ara-desktop', device === DeviceClass.Desktop);
		});
	}
}
